import { useEffect, useState } from 'react';
import { amountText } from '../assets.js';
import type { Platform } from '../platform/index.js';
import { toast } from '../toast.js';
import { sendNowAndWait, track } from '../track.js';
import {
  forgetStoredCopy,
  forgetWallet,
  listWallets,
  withWallet,
  type WalletEntry,
  type WalletSeen,
} from '../wallet.js';
import { ago } from './Market.js';
import { PinPad } from './PinPad.js';

/**
 * Taking a wallet off this device, after one look at what it holds and the
 * one thing that matters: only its twelve words open it again. The same card
 * in a browser and in Telegram. Local storage only; nothing leaves the device.
 */
export function ForgetWallet({
  platform,
  entry,
  isOpen,
  address,
  seen,
  live,
  onShowWords,
  onKeep,
  onRemoved,
}: {
  platform: Platform;
  entry: WalletEntry;
  /** The wallet signed in now: removing it asks for its PIN. */
  isOpen: boolean;
  /** Its Solana address, when known. */
  address: string;
  /** What it holds; null when this device has never seen it open. */
  seen: WalletSeen | null;
  /** The figures are from just now, not from when it was last open. */
  live: boolean;
  onShowWords: () => void;
  onKeep: () => void;
  onRemoved: (result: { wasOpen: boolean; next: string | null }) => void;
}) {
  const [step, setStep] = useState<'card' | 'pin'>('card');
  const [busy, setBusy] = useState(false);
  // Remove is a second, deliberate tap: a quick double tap on the row that
  // opened this card does not reach it.
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => {
      setArmed(true);
    }, 600);
    return () => {
      window.clearTimeout(t);
    };
  }, []);

  const remove = async (): Promise<void> => {
    const result = forgetWallet(entry.id);
    await forgetStoredCopy(platform, entry.sealed);
    track('wallet_forgotten', { open: result.wasOpen, left: listWallets().length });
    // Removing the open wallet reloads the page; the event goes first.
    await sendNowAndWait();
    onRemoved(result);
  };

  if (step === 'pin') {
    return (
      <div className="forget" data-testid="forget-pin">
        <div className="forget-title">Your PIN</div>
        <p className="forget-note">To remove {entry.label} from this device.</p>
        <PinPad
          title=""
          inline
          busy={busy}
          onBack={() => {
            setStep('card');
          }}
          onPin={(p) => {
            setBusy(true);
            withWallet(platform, p, () => true)
              .then(remove)
              .catch((e: unknown) => {
                toast.err(e instanceof Error ? e.message : String(e));
              })
              .finally(() => {
                setBusy(false);
              });
          }}
        />
      </div>
    );
  }

  const short = address === '' ? null : `${address.slice(0, 4)}…${address.slice(-4)}`;
  return (
    <div className="forget" data-testid="forget-card">
      <div className="forget-title">Remove from this device</div>
      <div className="forget-who">
        <span className="forget-name" data-testid="forget-name">
          {entry.label}
        </span>
        {short !== null && <span className="forget-addr">{short}</span>}
      </div>
      {seen === null ? (
        <p className="forget-note">Open it once on this device to see what it holds.</p>
      ) : (
        <>
          <dl className="forget-holds" data-testid="forget-holds">
            <div>
              <dt>Gaju</dt>
              <dd>{amountText('GAJU', BigInt(seen.gaju ?? '0'))}</dd>
            </div>
            <div>
              <dt>USDC</dt>
              <dd>{amountText('USDC', BigInt(seen.usdc ?? '0'))}</dd>
            </div>
            <div>
              <dt>XGE</dt>
              <dd>{amountText('XGE', BigInt(seen.xge ?? '0'))}</dd>
            </div>
            {seen.worth !== null && (
              <div className="forget-total">
                <dt>Total</dt>
                <dd>{amountText('USDC', BigInt(seen.worth))}</dd>
              </div>
            )}
            <div>
              <dt>Open orders</dt>
              <dd data-testid="forget-orders">{seen.orders}</dd>
            </div>
          </dl>
          {!live && <p className="forget-note">As of when it was last open, {ago(seen.at)}.</p>}
        </>
      )}
      <p className="forget-warn" data-testid="forget-warn">
        Removing it only takes it off this device. The wallet and everything in it stay on chain.
        The only way to open it again, here or anywhere, is its twelve words. If you have not
        written them down, do that first.
      </p>
      <button className="forget-btn" onClick={onShowWords} data-testid="forget-words">
        Show the words
      </button>
      <button
        className="forget-btn danger"
        disabled={!armed || busy}
        onClick={() => {
          if (isOpen) {
            setStep('pin');
            return;
          }
          setBusy(true);
          void remove().finally(() => {
            setBusy(false);
          });
        }}
        data-testid="forget-remove"
      >
        Remove
      </button>
      <button className="forget-btn plain" onClick={onKeep} data-testid="forget-keep">
        Keep it
      </button>
    </div>
  );
}
